import { Link, useRouterState } from '@tanstack/react-router';
import { cn } from '@/lib/utils/cn';
import { useMobileSidebar } from './use-mobile-sidebar';
import { useEffect } from 'react';
import { UserMenu } from './user-menu';
import { SidebarNav, SidebarNavItem } from './sidebar-nav';
import { IconButton } from '@/components/common';
import { useAuth } from '@/features/auth';
import snapesLogo from '@/assets/images/snapes-logo.png';

type SidebarIconComponent = (props: { className?: string }) => React.ReactElement;

const navItems: Array<{ label: string; href: string; icon: SidebarIconComponent }> = [
  {
    label: 'Dashboard',
    href: '/client/dashboard',
    icon: DASHBOARD,
  },
  {
    label: 'Quotes',
    href: '/client/quotes',
    icon: DESCRIPTION,
  },
  {
    label: 'Address Book',
    href: '/client/address-book',
    icon: LOCAL_SHIPPING,
  },
];

interface ClientSidebarProps {
  className?: string;
}

export function ClientSidebar({ className }: ClientSidebarProps) {
  const router = useRouterState();
  const { isMobileSidebarOpen, closeMobileSidebar, isDesktopSidebarExpanded } = useMobileSidebar();
  const auth = useAuth();

  // Close mobile sidebar when route changes
  useEffect(() => {
    closeMobileSidebar();
  }, [router.location.pathname, closeMobileSidebar]);

  const getCurrentPath = () => {
    return router.location.pathname;
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={closeMobileSidebar}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          // Fixed position on mobile, relative on desktop
          'fixed inset-y-0 left-0 z-50 flex flex-col border-r border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900',
          'transition-all duration-300 ease-in-out',
          // Mobile: always full width when open, hidden when closed
          'lg:relative lg:sticky lg:top-0',
          isMobileSidebarOpen ? 'translate-x-0 w-64' : '-translate-x-full lg:translate-x-0',
          // Desktop: expand/shrink
          isDesktopSidebarExpanded ? 'lg:w-64' : 'lg:w-20',
          'lg:h-screen',
          className,
        )}
      >
        <div className="relative px-4 pt-5 pb-4">
          <IconButton
            icon={<CLOSE className="h-5 w-5 text-gray-500" />}
            onClick={closeMobileSidebar}
            className="lg:hidden absolute top-3 right-3 shrink-0 hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label="Close sidebar"
          />
          <div className="flex flex-col items-center gap-2">
            <img src={snapesLogo} alt="Snapes Logistics" className="w-full h-auto object-contain" />
            <p className="text-sm font-semibold uppercase tracking-widest text-primary dark:text-primary-light">Client Portal</p>
          </div>
        </div>

        <SidebarNav
          items={navItems.map((item) => ({
            ...item,
            isActive: getCurrentPath().startsWith(item.href),
            icon: <item.icon className="h-6 w-6" />,
          }))}
          LinkComponent={Link}
          onItemClick={closeMobileSidebar}
          isExpanded={isDesktopSidebarExpanded || isMobileSidebarOpen}
          activeClassName="border-l-4 border-primary bg-primary/10 text-primary font-semibold"
          inactiveClassName="text-gray-700 hover:bg-primary/5 dark:text-gray-300 dark:hover:bg-gray-800"
        />

        <div className="border-t border-gray-200 p-4 dark:border-gray-800">
          {auth.user && (
            <UserMenu
              userName={auth.user.name || 'User'}
              userMeta={auth.user.email || ''}
              avatarUrl={auth.user.avatarUrl}
              variant="sidebar"
            />
          )}
        </div>
      </aside>
    </>
  );
}

// Icon components as simple SVGs
function HOME({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
      />
    </svg>
  );
}

function DASHBOARD({ className }: { className?: string }) {
  return (
    <span className={cn('material-symbols-outlined text-[24px] leading-none', className)}>dashboard</span>
  );
}

function DESCRIPTION({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  );
}


function LOCAL_SHIPPING({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  );
}

function CLOSE({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );
}
